class UUID {
  static v4() {
    return ('' + 1e7 + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c => {
      const r = crypto.getRandomValues(new Uint8Array(1))[0];
      const n = parseInt(c, 10);
      return (n ^ (r & (15 >> (n / 4)))).toString(16);
    });
  }
}

var __classPrivateFieldSet = (undefined && undefined.__classPrivateFieldSet) || function (receiver, privateMap, value) {
  if (!privateMap.has(receiver)) {
    throw new TypeError("attempted to set private field on non-instance");
  }
  privateMap.set(receiver, value);
  return value;
};
var __classPrivateFieldGet = (undefined && undefined.__classPrivateFieldGet) || function (receiver, privateMap) {
  if (!privateMap.has(receiver)) {
    throw new TypeError("attempted to get private field on non-instance");
  }
  return privateMap.get(receiver);
};
var _rgba;
class Color {
  constructor(color) {
    _rgba.set(this, void 0);
    const rgba = this._parseColor(color);
    if (rgba == null)
      throw new Error('Unknown color: ' + color);
    __classPrivateFieldSet(this, _rgba, rgba);
  }
  get hex() {
    const { rgba } = this;
    const toHex = (n) => Math.round(n).toString(16).padStart(2, '0');
    const base = `${toHex(rgba.r)}${toHex(rgba.g)}${toHex(rgba.b)}`;
    const alpha = rgba.a == 1 ? '' : toHex(rgba.a * 255);
    return `#${base}${alpha}`;
  }
  get rgba() {
    return __classPrivateFieldGet(this, _rgba);
  }
  static mix(c1, c2, weight = 0.5) {
    const { r: r1, g: g1, b: b1, a: a1 } = c1.rgba;
    const { r: r2, g: g2, b: b2, a: a2 } = c2.rgba;
    const w = 2 * weight - 1;
    const a = a1 - a2;
    const w1 = ((w * a == -1 ? w : (w + a) / (1 + w * a)) + 1) / 2;
    const w2 = 1 - w1;
    return new Color({
      r: w1 * r1 + w2 * r2,
      g: w1 * g1 + w2 * g2,
      b: w1 * b1 + w2 * b2,
      a: a1 * weight + a2 * (1 - weight)
    });
  }
  static multiply(c1, c2) {
    const { r: r1, g: g1, b: b1 } = c1.rgba;
    const { r: r2, g: g2, b: b2 } = c2.rgba;
    return new Color({
      r: (r1 * r2) / 255,
      g: (g1 * g2) / 255,
      b: (b1 * b2) / 255,
      a: 1
    });
  }
  mix(color) {
    __classPrivateFieldSet(this, _rgba, Object.assign({}, Color.multiply(this, color).rgba));
  }
  multiply(color) {
    __classPrivateFieldSet(this, _rgba, Object.assign({}, Color.multiply(this, color).rgba));
  }
  _parseColor(color) {
    if (color instanceof Color)
      return color.rgba;
    if (typeof color !== 'string')
      return color;
    return color.startsWith('#')
      ? this._parseHex(color)
      : this._parseRgba(color);
  }
  _parseHex(hex) {
    const shortRegex = /^#([a-f\d])([a-f\d])([a-f\d])([a-f\d])?$/i;
    const full = hex.replace(shortRegex, (_, r, g, b, a) => {
      const opacity = a !== null && a !== void 0 ? a : 'f';
      return '#' + r + r + g + g + b + b + opacity + opacity;
    });
    const fullRegex = /^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i;
    var result = fullRegex.exec(full);
    if (!result)
      return null;
    const alpha = parseInt(result[4], 16);
    return {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
      a: isNaN(alpha) ? 1 : alpha / 255
    };
  }
  _parseRgba(rgba) {
    const regex = /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d\.]+)(%)?)?\)$/;
    const match = rgba.match(regex);
    if (!match)
      return null;
    return {
      r: parseInt(match[1], 10),
      g: parseInt(match[2], 10),
      b: parseInt(match[3], 10),
      a: match[5] !== '%' ? parseFloat(match[4]) : parseInt(match[4], 10) / 100
    };
  }
}
_rgba = new WeakMap();
const colorShade = (color1, color2 = '#fff', mix = '#fff') => {
  const c1 = new Color(color1);
  const c2 = new Color(color2);
  const c3 = new Color(mix);
  if (!c1 || !c2 || !c3)
    return null;
  const mixL = c3;
  const mixD = Color.multiply(c1, c1);
  return {
    c300: Color.mix(c1, mixL, 0.7).hex,
    c400: Color.mix(c1, mixL, 0.85).hex,
    c500: c1.hex,
    c600: Color.mix(c1, mixD, 0.87).hex,
    c700: Color.mix(c1, mixD, 0.7).hex,
    con1: new Color(Object.assign(Object.assign({}, c2.rgba), { a: 0.87 })).hex,
    con2: new Color(Object.assign(Object.assign({}, c2.rgba), { a: 0.6 })).hex,
    con3: new Color(Object.assign(Object.assign({}, c2.rgba), { a: 0.38 })).hex,
    con4: new Color(Object.assign(Object.assign({}, c2.rgba), { a: 0.1 })).hex
  };
};
const getColorShade = (el, name) => {
  const style = getComputedStyle(el);
  const color1 = style.getPropertyValue(`--bce-c500-${name}`).trim();
  const color2 = style.getPropertyValue(`--bce-con1-${name}`).trim();
  return `${color1} ${new Color(Object.assign(Object.assign({}, new Color(color2).rgba), { a: 1 })).hex}`;
};
const setColorShade = (el, name, colors, mix) => {
  const [color1, color2] = colors.split(' ');
  const shades = colorShade(color1, color2, mix);
  for (const shade of Object.keys(shades || {})) {
    const color = shades[shade];
    el.style.setProperty(`--bce-${shade}-${name}`, color);
  }
};

export { Color as C, UUID as U, colorShade as c, getColorShade as g, setColorShade as s };
